import React from "react";
import { ChevronLeft, ChevronRight, X, BookOpen } from "lucide-react";

// Real company profiles we designed (pages exported from the delivered PDFs).
const files = import.meta.glob("@images/profiles/*.webp", { eager: true, import: "default" });

const PROFILES = [
    { key: "future-core", name: "نواة المستقبل", field: "تنظيم الفعاليات" },
    { key: "qwaed", name: "قواعد للتكنولوجيا", field: "تقنية المعلومات" },
    { key: "level-art", name: "ليفيل آرت", field: "مقاولات" },
    { key: "nbr-alarqam", name: "نبر الأرقام", field: "خدمات مالية وإدارية" },
    { key: "waha", name: "واحة", field: "سفر وسياحة" },
    { key: "eraco", name: "ERACO", field: "شركات" },
    { key: "etanergy", name: "Etanergy", field: "طاقة" },
    { key: "cjs", name: "CJS", field: "شركات" },
    { key: "cold-climate", name: "Cold Climate", field: "تكييف وتبريد" },
    { key: "anwar-tathrib", name: "أنوار تثريب", field: "شركات" },
]
    .map((p) => ({
        ...p,
        pages: Object.entries(files)
            .map(([path, url]) => ({ m: path.match(new RegExp(`/${p.key}-(\\d+)\\.webp$`)), url }))
            .filter((x) => x.m)
            .sort((a, b) => +a.m[1] - +b.m[1])
            .map((x) => x.url),
    }))
    .filter((p) => p.pages.length > 0);

const Viewer = ({ profile, onClose }) => {
    const [i, setI] = React.useState(0);
    const last = profile.pages.length - 1;
    React.useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") onClose();
            if (e.key === "ArrowLeft") setI((v) => Math.min(last, v + 1));
            if (e.key === "ArrowRight") setI((v) => Math.max(0, v - 1));
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [last, onClose]);
    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-3" style={{ background: "rgba(2,6,23,0.9)" }} dir="rtl" onClick={onClose}>
            <div className="w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
                <div className="mb-3 flex items-center justify-between text-white">
                    <p className="text-sm font-bold">
                        {profile.name} <span className="text-white/60">· {i + 1} / {profile.pages.length}</span>
                    </p>
                    <button type="button" onClick={onClose} aria-label="إغلاق" className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 hover:bg-white/20">
                        <X className="h-5 w-5" />
                    </button>
                </div>
                <img src={profile.pages[i]} alt={`${profile.name} - صفحة ${i + 1}`} className="w-full rounded-xl bg-white object-contain shadow-2xl" />
                <div className="mt-3 flex items-center justify-center gap-3">
                    <button type="button" disabled={i === 0} onClick={() => setI(i - 1)} className="flex h-11 w-11 items-center justify-center rounded-full bg-white text-slate-900 disabled:opacity-30" aria-label="السابق">
                        <ChevronRight className="h-5 w-5" />
                    </button>
                    <button type="button" disabled={i === last} onClick={() => setI(i + 1)} className="flex h-11 w-11 items-center justify-center rounded-full bg-white text-slate-900 disabled:opacity-30" aria-label="التالي">
                        <ChevronLeft className="h-5 w-5" />
                    </button>
                </div>
            </div>
        </div>
    );
};

const ProfileWorksSection = () => {
    const [open, setOpen] = React.useState(null);
    if (PROFILES.length === 0) return null;
    return (
        <section className="bg-white py-10 md:py-16" dir="rtl">
            <div className="wrapper">
                <div className="mb-6 text-right md:mb-8">
                    <span className="inline-flex rounded-full bg-blue-50 px-3 py-1.5 text-xs font-bold text-blue-600">من أعمالنا</span>
                    <h2 className="mt-3 text-2xl font-black text-slate-900 md:text-3xl">
                        بروفايلات <span className="text-blue-600">صممناها لعملائنا</span>
                    </h2>
                    <p className="mt-2 text-sm text-slate-500 md:text-base">اضغط على أي بروفايل وتصفّح صفحاته.</p>
                </div>
                <div className="grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-5 lg:grid-cols-4">
                    {PROFILES.map((p) => (
                        <button
                            key={p.key}
                            type="button"
                            onClick={() => setOpen(p)}
                            className="group overflow-hidden rounded-2xl border border-gray-100 bg-white text-right shadow-sm transition-shadow hover:shadow-md"
                        >
                            <div className="relative aspect-[3/2] overflow-hidden bg-slate-100">
                                <img src={p.pages[0]} alt={`بروفايل ${p.name}`} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                                <span className="absolute bottom-2 left-2 inline-flex items-center gap-1 rounded-full bg-white/95 px-2.5 py-1 text-[11px] font-bold text-slate-700 shadow">
                                    <BookOpen className="h-3.5 w-3.5" />
                                    {p.pages.length} صفحات
                                </span>
                            </div>
                            <div className="p-3">
                                <p className="text-sm font-black text-slate-900">{p.name}</p>
                                <p className="text-xs text-slate-500">{p.field}</p>
                            </div>
                        </button>
                    ))}
                </div>
            </div>
            {open && <Viewer profile={open} onClose={() => setOpen(null)} />}
        </section>
    );
};

// The portfolio's "ملفات وبروفايل شركات" tab shows this same section and adds
// this count to its tab badge.
export const PROFILE_WORKS_COUNT = PROFILES.length;

export default ProfileWorksSection;
